import { useEffect, useState, useCallback, useMemo } from "react";
import { Palette, Merge, RefreshCw, Search, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ColorSwatch } from "@/lib/colors";

/**
 * Pantalla de gestión del diccionario de colores del catálogo.
 *
 * Objetivo: detectar y fusionar colores duplicados por typo o variaciones
 * ("ROSA CHICLE" vs "ROSA CHICLES", "Amarillo Pastel" vs "amarillo pastel").
 *
 * Solo director.
 */
export default function ColoresCatalogoPage() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fusion, setFusion] = useState(null); // { origen: {color, count, foto}, destino: string }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/productos/colores");
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el diccionario de colores");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const items_filtrados = useMemo(() => {
    if (!q.trim()) return items;
    const t = q.trim().toLowerCase();
    return items.filter((c) => (c.color || "").toLowerCase().includes(t));
  }, [items, q]);

  // Sugerencia de duplicados: normaliza cada color y agrupa
  const duplicados = useMemo(() => {
    const norm = (s) => (s || "")
      .toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/s\b/g, "") // ignorar plural simple ("chicles" → "chicle")
      .replace(/[^a-z0-9]/g, "");
    const groups = {};
    items.forEach((c) => {
      const k = norm(c.color);
      groups[k] = groups[k] || [];
      groups[k].push(c);
    });
    return Object.values(groups).filter((g) => g.length > 1);
  }, [items]);

  const abrirFusion = (origen, destinoSugerido = null) => {
    setFusion({ origen, destino: destinoSugerido || "" });
  };

  const confirmarFusion = async () => {
    if (!fusion?.destino?.trim()) {
      toast.error("Ingresa el color destino");
      return;
    }
    try {
      const { data } = await api.post("/productos/colores/fusionar", {
        color_origen: fusion.origen.color,
        color_destino: fusion.destino.trim(),
      });
      toast.success(`${data.actualizados} productos renombrados a "${data.color_destino}"`);
      setFusion(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo fusionar");
    }
  };

  return (
    <div className="space-y-4" data-testid="colores-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-display font-bold flex items-center gap-2">
            <Palette className="w-6 h-6 text-primary" /> Diccionario de colores
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Todos los colores únicos usados en el catálogo. Fusiona duplicados por typo
            (ej "ROSA CHICLE" vs "ROSA CHICLES") con un clic. La fusión renombra todos los
            productos afectados en la BD.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar color…" className="pl-9 h-9 w-56" data-testid="colores-buscar" />
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="colores-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refrescar
          </Button>
        </div>
      </div>

      {/* Panel de sugerencias de duplicados */}
      {duplicados.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 space-y-2" data-testid="colores-duplicados-panel">
          <div className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
            <Merge className="w-4 h-4" /> Posibles duplicados detectados ({duplicados.length} grupo{duplicados.length === 1 ? "" : "s"})
          </div>
          <div className="text-xs text-amber-800/80">Colores que parecen ser el mismo tono con diferente escritura o plural.</div>
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {duplicados.map((grupo, i) => {
              const canonico = grupo.reduce((best, c) => (c.count > best.count ? c : best), grupo[0]);
              return (
                <div key={i} className="bg-white border border-amber-200 rounded-md p-2 flex items-center gap-2 flex-wrap">
                  {grupo.map((c) => (
                    <span key={c.color} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs ${c.color === canonico.color ? "bg-emerald-100 text-emerald-800 border border-emerald-300 font-semibold" : "bg-muted"}`}>
                      <ColorSwatch nombre={c.color} size={10} />
                      {c.color} <span className="text-[10px] opacity-70">({c.count})</span>
                    </span>
                  ))}
                  <div className="ml-auto flex items-center gap-2">
                    <span className="text-[11px] text-muted-foreground">Sugerido: fusionar a</span>
                    <Button size="sm" variant="outline" onClick={() => {
                      grupo.filter((c) => c.color !== canonico.color).forEach((c) => abrirFusion(c, canonico.color));
                      // Como abrirFusion sobrescribe fusion state, solo abre el primero.
                      // El usuario confirma uno a uno.
                    }} data-testid={`fusionar-grupo-${i}`}>
                      <ArrowRight className="w-3 h-3 mr-1" /> {canonico.color}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground py-10 text-center">Cargando…</div>
      ) : items_filtrados.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-10 text-center">
          <Palette className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
          <div className="text-sm font-medium text-muted-foreground">
            {items.length === 0 ? "No hay productos con color asignado." : "Ningún color coincide."}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {items_filtrados.map((c) => (
            <div key={c.color} className="border border-border rounded-lg overflow-hidden bg-card hover:shadow-md transition-shadow" data-testid={`color-card-${c.color.substring(0, 20)}`}>
              <div className="aspect-video bg-muted flex items-center justify-center border-b border-border/60">
                {c.foto_producto_id ? (
                  <img src={`${API}/api/catalogo/img/${c.foto_producto_id}?w=200`} alt="" className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <ColorSwatch nombre={c.color} size={56} />
                )}
              </div>
              <div className="p-2.5 space-y-1">
                <div className="flex items-center gap-1.5">
                  <ColorSwatch nombre={c.color} size={12} />
                  <div className="text-sm font-semibold truncate" title={c.color}>{c.color}</div>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  <b className="text-foreground">{c.count}</b> productos · {c.familias_count} familias
                </div>
                <Button variant="outline" size="sm" className="w-full h-7 text-[10px]" onClick={() => abrirFusion(c)} data-testid={`fusionar-color-${c.color.substring(0, 20)}`}>
                  <Merge className="w-3 h-3 mr-1" /> Fusionar
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal fusión */}
      <Dialog open={!!fusion} onOpenChange={(v) => !v && setFusion(null)}>
        <DialogContent className="max-w-md" data-testid="fusion-modal">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Merge className="w-5 h-5 text-primary" /> Fusionar color
            </DialogTitle>
          </DialogHeader>
          {fusion && (
            <div className="space-y-3">
              <div className="p-3 bg-muted rounded-md text-sm">
                <div className="text-[10px] uppercase text-muted-foreground font-bold">Renombrar</div>
                <div className="flex items-center gap-2 mt-1">
                  <ColorSwatch nombre={fusion.origen.color} size={14} />
                  <span className="font-semibold">{fusion.origen.color}</span>
                  <span className="text-xs text-muted-foreground">({fusion.origen.count} productos)</span>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-muted-foreground">Nuevo nombre del color</label>
                <Input
                  value={fusion.destino}
                  onChange={(e) => setFusion({ ...fusion, destino: e.target.value })}
                  placeholder="Ej: Rosa Chicle"
                  className="mt-1"
                  autoFocus
                  data-testid="fusion-destino-input"
                />
                <div className="text-[10px] text-muted-foreground mt-1">
                  Si el nombre destino ya existe, los productos se consolidan bajo ese color.
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <Button variant="ghost" onClick={() => setFusion(null)}>Cancelar</Button>
                <Button onClick={confirmarFusion} data-testid="fusion-confirmar">Fusionar {fusion.origen.count} productos</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
